import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {REPORT_BASICS_LINKS, ReportName} from 'common/report/constants';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {QueryBuilderParams, QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {ConversionQueryOptionsEl} from 'common/query-builder/conversion-query-options';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {ReportQueryBuilder} from 'common/report/report-manager/query-builder';
import {FUNNELS_DEFAULT_EVENT, FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {
  attributionItems,
  conversionQueryOptionsUpdate,
  getConversionQueryOptionsAttrs,
  MaxNumGroupBys,
  queryBuilderUpdate,
} from 'common/report/funnels/query-builder/utils';
import {ChartType, FunnelBookmark, FunnelsAggregateType, TrendChartType} from 'common/report/funnels/models/types';
import {emitTrackEvent} from 'common/util/dom';
import {TrackedEvents} from 'common/report/funnels/util/tracking';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {BehaviorType} from 'common/query-builder/query-block/constants';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {
  CountBy,
  FunnelsCountType,
  QueryEventEntry,
  QuerySuperEntry,
  RowLabelType,
} from 'common/query-builder/query-entry-section/types';
import {BuilderParams} from 'common/query-builder/conversion-query-options/types';
import {stepSelectorAttributes} from 'common/report/funnels/visualization/util';
import {useFunnelsConsistency} from 'common/report/util/feature-sampling';
import {generateStepRanges} from 'common/widgets/step-range-selector/util';
import {
  FunnelsCountToFrequencyMapping,
  FunnelsCountToMetricColumnMapping,
  FunnelsCountToTrendMapping,
} from 'common/query-builder/query-block/count-menu/constants';
import {isAdvancedValueRangeSelection} from 'common/query-builder/query-block/count-menu/util';

import {FunnelQueryResponse} from '../models/api-types';

class FunnelsQueryBuilder extends ReportQueryBuilder<FunnelBookmark, FunnelQueryResponse> {
  displayContext = SearchDisplayContext.Funnels;
  reportType = ReportName.Funnels;

  qbChildrenTemplate() {
    if (!this.params.bookmarkParams || !this.params.project) {
      return null;
    }
    const funnelsQuery = this.funnelsQuery;
    const queryBuilderParams: BuilderParams = {
      exclusionSteps: funnelsQuery.exclusionSteps,
      entries: funnelsQuery.events,
      dataGroupsEnabled: this.canUseDataGroups(),
      projectId: this.params.projectId,
      workspaceId: this.params.workspaceId,
      dataGroupId: funnelsQuery.dataGroupId,
      projectHasPeople: this.params.project.hasIntegratedEngage,
      projectHasCohorts: this.canUseCohorts(),
      trackName: this.reportType,
      projectAsOfDate: this.params.project.projectAsOfDate,
      entryRowLabelType: RowLabelType.Numeric,
    };

    return (
      <>
        {/*Conversion details section*/}
        <ConversionQueryOptionsEl
          attrs={getConversionQueryOptionsAttrs({
            projectId: this.params.projectId,
            workspaceId: this.params.workspaceId,
            funnelsQuery: this.funnelsQuery,
            queryBuilderParams,
          })}
          on={{
            changeQueryOption: ({detail: {options}}) => {
              const previousFunnelQuery = this.funnelsQuery;
              if (previousFunnelQuery.funnelOrder !== options.funnelOrder) {
                emitTrackEvent(this, {eventName: TrackedEvents.AnyOrderToggled});
              }
              const paramsToUpdate = conversionQueryOptionsUpdate(options, previousFunnelQuery);
              this.dispatchUpdate({
                params: FunnelsQuery.createFromUpdate(previousFunnelQuery, paramsToUpdate).generateBookmark(),
                queryReason: QueryReason.QbOtherUpdate,
              });
            },
            track: ({detail}) =>
              emitTrackEvent(this, {eventName: detail.eventName, props: detail.properties || detail.props}),
          }}
        />
      </>
    );
  }

  reportSpecificQBParams(): Partial<QueryBuilderParams> {
    const funnelsQuery = this.funnelsQuery;
    const canUseDataGroups = this.canUseDataGroups();
    return {
      addEntryLabel: `Select Step`,
      addEntryButtonTooltipText: `Analyze users who performed each event.\n[Read Intro to Funnels](${
        REPORT_BASICS_LINKS[ReportName.Funnels]
      })`,
      canCustomizeNumericBuckets: true,
      canGroupEvents: true,
      dateRange: funnelsQuery.dateRange.getAttributes(),
      enableEnhancedListFilters: true,
      entries: funnelsQuery.events,
      headerTitle: `Steps`,
      hideDatePicker: true,
      enableFilterAttribution: true,
      entriesDraggable: true,
      includeCustomProperties: `all`,
      exclusionSteps: funnelsQuery.exclusionSteps,
      exclusionStepAddEntryTooltipText: `Exclude users in between steps who performed exclusion events.`,
      filters: funnelsQuery.filters,
      groupBy: funnelsQuery.groupBy,
      groupByLimitation: MaxNumGroupBys,
      disableBreakdown: funnelsQuery.chartType === ChartType.Sankey,
      otherGroupByOptions: funnelsQuery.isAnyOrderFunnel() ? [`$paths`] : [],
      showEntryRename: true,
      supportB2bCohorts: canUseDataGroups,
      breakdownAttribution: attributionItems(funnelsQuery),
      showSpecialDateProperty: true,
      minEntries: 2,
      emptyPlaceholder: FUNNELS_DEFAULT_EVENT as QuerySuperEntry,
      ...(funnelsQuery.dataGroupId ? {dataGroupId: funnelsQuery.dataGroupId} : null),
      ...(funnelsQuery.isAnyOrderFunnel() ? {entryIndexToggleIcon: `any-order`, entryIndexToggleable: true} : null),
    };
  }

  get funnelsQuery() {
    return FunnelsQuery.fromBookmark(this.params.bookmarkParams);
  }

  getActiveDataGroup() {
    const dataGroupId = this.funnelsQuery.dataGroupId;
    if (dataGroupId && dataGroupStore.value) {
      return dataGroupStore.value[dataGroupId];
    }
    return null;
  }

  bookmarkUpdateFromQbUpdate(detail: QueryBuilderUpdateDetail<QueryEventEntry>): FunnelBookmark {
    const curFunnelsQuery = this.funnelsQuery;
    const {query: updatedQuery} = queryBuilderUpdate(detail, curFunnelsQuery, false);
    return FunnelsQuery.createFromUpdate(curFunnelsQuery, updatedQuery).generateBookmark();
  }

  getCombinedCountType() {
    // TODO somehow clean this up
    const query = this.funnelsQuery;
    if (!useFunnelsConsistency()) {
      return query.countType;
    }

    switch (query.trendType) {
      case TrendChartType.BottomOfFunnel:
      case TrendChartType.TopOfFunnel:
        return query.countType;
      case TrendChartType.Conversion:
        return FunnelsCountType.ConversionRate;
      case TrendChartType.TimeToConvert:
        return FunnelsCountType.TimeToConvert;
    }
  }

  handleCountByChanged(ev: CustomEvent<{item: CountBy}>) {
    // We have two separate concepts being represented as a single value atm...
    // (what we consider a funnel, vs what we are calculating from the funnel)
    // qualifier type is what we consider a funnel, countType is what we are calculating
    const countType = ev.detail.item.countType as FunnelsCountType;
    let qualifierType = countType;

    if (![FunnelsCountType.Unique, FunnelsCountType.General, FunnelsCountType.Session].includes(qualifierType)) {
      qualifierType = FunnelsCountType.General;
    }

    const prevCountType = this.funnelsQuery.countType;
    let conversionWindowUpdates = null;
    if (
      [FunnelsCountType.Unique, FunnelsCountType.General].includes(prevCountType) &&
      qualifierType === FunnelsCountType.Session
    ) {
      conversionWindowUpdates = {value: 1, unit: `session`};
    } else if (
      FunnelsCountType.Session === prevCountType &&
      [FunnelsCountType.Unique, FunnelsCountType.General].includes(qualifierType)
    ) {
      conversionWindowUpdates = {value: 30, unit: `day`};
    }

    let trendType = FunnelsCountToTrendMapping.get(countType);

    // TODO abstract away top of funnel and bottom of funnel so we don't need this
    if (trendType === TrendChartType.TopOfFunnel && isNaN(this.funnelsQuery.eventIndex)) {
      trendType = TrendChartType.BottomOfFunnel;
    }

    const updates: Partial<FunnelsQuery> = {
      frequencyAggregateType: FunnelsAggregateType.Distribution, // TODO hard-coded for now, no selector
      countType: qualifierType,
      trendType: FunnelsCountToTrendMapping.get(countType),
      frequencyType: FunnelsCountToFrequencyMapping.get(countType),
      selectedTableColumns: {
        ...this.funnelsQuery.selectedTableColumns,
        [FunnelsCountToMetricColumnMapping.get(countType)]: true, // TODO should not do this ephemerally
      },
      ...(conversionWindowUpdates ? {conversionWindow: conversionWindowUpdates} : {}),
    };

    // Step Selection
    const stepValue = ev.detail.item.advancedMenuValue;
    if (stepValue !== undefined && isAdvancedValueRangeSelection(stepValue)) {
      updates.eventIndex =
        typeof stepValue === `number` ? stepValue : stepValue.isEntireRange ? null : stepValue.range.from - 1;
    }

    this.dispatchUpdate({
      params: FunnelsQuery.createFromUpdate(this.funnelsQuery, updates).generateBookmark(),
      queryReason: QueryReason.QbOtherUpdate,
      requery: true,
    });
  }

  protected qbCountMenuTemplate(): VNode {
    const query = this.funnelsQuery;
    const stepSelectorAttrs = stepSelectorAttributes(query);
    return (
      <QueryBlockEl
        sel='.count-menu'
        attrs={{
          'advanced-menu-sections':
            query.chartType === ChartType.Chart
              ? []
              : [
                  {
                    icon: `info-xs`,
                    tooltip: {text: `Select the completed step to measure from`},
                    label: `Completed Steps`,
                    items: generateStepRanges({
                      entry: stepSelectorAttrs.selected,
                      steps: stepSelectorAttrs[`num-steps`],
                      rowLabelType: stepSelectorAttrs[`row-label-type`] as RowLabelType,
                      disableAllSteps: stepSelectorAttrs[`disable-all-steps`],
                      singleStep: true,
                    }),
                  },
                ],
          elref: `countBlock`,
          type: `count`,
          'behavior-type': BehaviorType.Funnel,
          'data-group-id': query.dataGroupId,
          'project-id': this.params.projectId,
          'selector-type': `button`,
          selected: {
            countType: this.getCombinedCountType(),
          },
          'highlight-selections': true,
          theme: SelectTheme.QueryBuilder,
          'button-size': ButtonSize.Small,
          'track-name': `funnels`,
          'user-id': this.params.userId,
          'workspace-id': this.params.workspaceId,
        }}
        on={{
          select: this.handleCountByChanged.bind(this),
        }}
      />
    );
  }
}

export const FunnelsQueryBuilderEl = defineAndMakeJsxFactory(`funnels-query-builder`, FunnelsQueryBuilder);
export const FunnelsQueryBuilderReact = panel2react(`funnels-query-builder`, FunnelsQueryBuilder);
